/**
 * @fileoverview Walkthrough Overlay
 * @description SVG-based spotlight overlay that highlights target elements
 * during walkthrough steps. Uses a cutout approach for robust highlighting
 * across all backgrounds (inspired by Shepherd.js SVG approach).
 * @module features/walkthrough/components/WalkthroughOverlay
 */

import { useEffect, useState, useCallback } from 'react';
import { cn } from '@flowstate/utils';

interface WalkthroughOverlayProps {
  /** CSS selector for the target element to highlight */
  targetSelector?: string;
  /** Whether the overlay is visible */
  visible: boolean;
  /** Padding around the highlighted element */
  padding?: number;
  /** Border radius of the cutout */
  borderRadius?: number;
  /** Click handler for the overlay backdrop */
  onBackdropClick?: () => void;
  /** Additional class names */
  className?: string;
}

interface TargetRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export function WalkthroughOverlay({
  targetSelector,
  visible,
  padding = 8,
  borderRadius = 8,
  onBackdropClick,
  className,
}: WalkthroughOverlayProps) {
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);

  const updateTargetRect = useCallback(() => {
    if (!targetSelector) {
      setTargetRect(null);
      return;
    }

    const element = document.querySelector(targetSelector);
    if (element) {
      const rect = element.getBoundingClientRect();
      setTargetRect({
        x: rect.left - padding,
        y: rect.top - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });
    } else {
      setTargetRect(null);
    }
  }, [targetSelector, padding]);

  useEffect(() => {
    if (!visible) {return;}

    updateTargetRect();

    // Update on scroll/resize
    const handleUpdate = () => updateTargetRect();
    window.addEventListener('resize', handleUpdate);
    window.addEventListener('scroll', handleUpdate, true);

    return () => {
      window.removeEventListener('resize', handleUpdate);
      window.removeEventListener('scroll', handleUpdate, true);
    };
  }, [visible, updateTargetRect]);

  if (!visible) {return null;}

  const viewBox = `0 0 ${window.innerWidth} ${window.innerHeight}`;

  return (
    <div
      className={cn(
        'fixed inset-0 z-[9998] transition-opacity duration-300',
        visible ? 'opacity-100' : 'opacity-0 pointer-events-none',
        className
      )}
      onClick={onBackdropClick}
      aria-hidden="true"
    >
      <svg
        className="absolute inset-0 w-full h-full"
        viewBox={viewBox}
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <mask id="walkthrough-mask">
            <rect width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.x}
                y={targetRect.y}
                width={targetRect.width}
                height={targetRect.height}
                rx={borderRadius}
                ry={borderRadius}
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          width="100%"
          height="100%"
          fill="rgba(0, 0, 0, 0.5)"
          mask="url(#walkthrough-mask)"
        />
        {targetRect && (
          <rect
            x={targetRect.x}
            y={targetRect.y}
            width={targetRect.width}
            height={targetRect.height}
            rx={borderRadius}
            ry={borderRadius}
            fill="none"
            stroke="hsl(var(--primary))"
            strokeWidth="2"
            className="animate-pulse"
          />
        )}
      </svg>
    </div>
  );
}
